import { FC, ReactElement, useContext, useEffect, useState } from 'react';
import { isEmpty } from 'lodash-es';
import { FormProvider, useForm } from 'react-hook-form';
import { CloseButton, Drawer, Flex, Spacer, Stack } from '@chakra-ui/react';

import { DocumentListContext } from './application';
import { Button } from './Button.component';

type ApplicationsFiltersDrawerProps = {
  contentComponent: ReactElement;
};

export const ApplicationsFiltersDrawer: FC<ApplicationsFiltersDrawerProps> = ({
  contentComponent,
}) => {
  const {
    setFilters,
    filters,
    filtersDrawerApi,
    selectedOptions,
    setSelectedOptions,
  } = useContext(DocumentListContext);
  const [formResetCount, setFormResetCount] = useState<number>(0);
  const form = useForm<Record<string, any>>();

  const onSubmit = (values: any): void => {
    setFilters(values);
    filtersDrawerApi.onClose();
  };

  const onReset = (): void => {
    form.reset();

    const contextSelectedOptions = selectedOptions;
    const contextFilters = filters;

    for (const variableKey in contextFilters) {
      if (variableKey in contextFilters) {
        contextFilters[variableKey] = undefined;
      }
    }
    setFilters(contextFilters);
    for (const variableSelectedOption in contextSelectedOptions) {
      if (variableSelectedOption in contextSelectedOptions) {
        setSelectedOptions(variableSelectedOption, []);
      }
    }
    setFormResetCount((prev) => prev + 1);
  };

  useEffect(() => {
    if (isEmpty(filters)) {
      form.reset();
    }

    Object.keys(filters).forEach((key) => {
      form.setValue(key, filters[key], { shouldTouch: true });
    });
  }, [filters]);

  return (
    <Drawer.Root
      onOpenChange={filtersDrawerApi.onOpenChange}
      open={filtersDrawerApi.open}
      placement="start"
    >
      <Drawer.Backdrop />

      <FormProvider {...form}>
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.CloseTrigger asChild>
              <CloseButton />
            </Drawer.CloseTrigger>

            <Drawer.Header borderBottomColor="gray.200" borderBottomWidth="1px">
              Filters
            </Drawer.Header>

            <Drawer.Body>
              <Flex
                as="form"
                direction="column"
                flex={1}
                id="applications-filters"
                onSubmit={form.handleSubmit(onSubmit)}
                p={2}
              >
                <Stack key={formResetCount} gap={6}>
                  {contentComponent}
                </Stack>
              </Flex>
            </Drawer.Body>

            <Drawer.Footer
              alignItems="center"
              borderTopWidth="1px"
              justifyContent="space-between"
              pl={0}
            >
              <Button
                color="black"
                fontSize="md"
                fontWeight="normal"
                onClick={onReset}
                size="sm"
                variant="ghost"
              >
                Clear filters
              </Button>

              <Spacer />

              <Button
                colorPalette="primary"
                fontSize="md"
                fontWeight="normal"
                form="applications-filters"
                size="md"
                type="submit"
                variant="solid"
              >
                Save
              </Button>
            </Drawer.Footer>
          </Drawer.Content>
        </Drawer.Positioner>
      </FormProvider>
    </Drawer.Root>
  );
};
